import { useEffect, useState } from "react";
import { Box, Collapsible, Text } from "@chakra-ui/react";
import { HiChevronDown } from "react-icons/hi2";

export const SectionCard = ({ children, ...props }) => (
    <Box bg="white" borderRadius={12} p={5} border="1px solid" borderColor="gray.200" {...props}>
        {children}
    </Box>
);

export const ItemCard = ({ children, ...props }) => (
    <Box
        bg="gray.50"
        borderRadius={8}
        p={4}
        border="1px solid"
        borderColor="gray.200"
        borderLeftWidth="3px"
        borderLeftColor="purple.200"
        transition="border-color 0.2s, box-shadow 0.2s"
        animation="fadeSlideIn 0.2s ease-out"
        _hover={{ borderLeftColor: "purple.400", boxShadow: "0 2px 10px rgba(23,21,31,0.06)" }}
        {...props}
    >
        {children}
    </Box>
);

/**
 * A collapsible form section. Replaces the old SectionCard + SectionHeader pair so long
 * steps read as a scannable outline instead of one continuous wall of fields. No number
 * badge here on purpose -- the left-hand step navigation already numbers things, and a
 * second, differently-scoped numbering inside each section read as confusing/incorrect.
 * `forceOpen` re-expands the section (without permanently locking it) whenever it starts
 * carrying a validation error, so a collapsed section can never hide a problem from the user.
 */
export const CollapsibleSection = ({ title, subtitle, icon: Icon, defaultOpen = false, forceOpen = false, action, children }) => {
    const [open, setOpen] = useState(defaultOpen);

    useEffect(() => {
        if (forceOpen) setOpen(true);
    }, [forceOpen]);

    return (
        <Box
            bg="white"
            borderRadius={12}
            border="1px solid"
            borderColor={open ? "purple.200" : "gray.200"}
            overflow="hidden"
            transition="border-color 0.2s"
        >
            <Collapsible.Root open={open} onOpenChange={(e) => setOpen(e.open)}>
                <Collapsible.Trigger asChild>
                    <Box
                        as="button"
                        type="button"
                        aria-expanded={open}
                        w="full"
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={3}
                        px={5}
                        py={4}
                        textAlign="left"
                        _hover={{ bg: "gray.50" }}
                        _active={{ bg: "gray.50", transform: "scale(0.997)" }}
                        transition="background 0.15s, transform 0.1s"
                    >
                        <Box display="flex" alignItems="center" gap={3} minW={0}>
                            {Icon && (
                                <Box
                                    flexShrink={0}
                                    color={open ? "purple.500" : "purple.400"}
                                    display="flex"
                                    aria-hidden="true"
                                    transition="color 0.2s"
                                >
                                    <Icon size={18} />
                                </Box>
                            )}
                            <Box minW={0}>
                                <Text color="gray.900" fontWeight={700} fontSize="sm">{title}</Text>
                                {subtitle && <Text fontSize="xs" color="gray.500" mt={0.5}>{subtitle}</Text>}
                            </Box>
                        </Box>
                        <Box display="flex" alignItems="center" gap={2} flexShrink={0}>
                            {action}
                            <Box
                                aria-hidden="true"
                                color="gray.400"
                                display="flex"
                                transform={open ? "rotate(180deg)" : "none"}
                                transition="transform 0.25s ease"
                            >
                                <HiChevronDown size={18} />
                            </Box>
                        </Box>
                    </Box>
                </Collapsible.Trigger>
                <Collapsible.Content>
                    <Box px={5} pb={6} pt={4} borderTop="1px solid" borderColor="gray.100">
                        {children}
                    </Box>
                </Collapsible.Content>
            </Collapsible.Root>
        </Box>
    );
};

export const SectionHeader = ({ number, title, subtitle, action }) => (
    <Box display="flex" alignItems="center" justifyContent="space-between" mb={5}>
        <Box display="flex" alignItems="center" gap={3}>
            {number !== undefined && (
                <Box
                    flexShrink={0}
                    w={6}
                    h={6}
                    borderRadius="full"
                    bg="purple.100"
                    color="purple.600"
                    fontSize="xs"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    fontWeight={600}
                >
                    {number}
                </Box>
            )}
            <Box>
                <Text color="gray.900" fontWeight={700}>{title}</Text>
                {subtitle && <Text fontSize="xs" color="gray.500" mt={0.5}>{subtitle}</Text>}
            </Box>
        </Box>
        {action}
    </Box>
);

export const Divider = (props) => <Box as="hr" borderColor="gray.200" my={5} {...props} />;

/**
 * Standardised container for a cluster of related on/off switches. Every step used to lay
 * these out with a one-off gridTemplateColumns per section (2, 3 or 4 columns, different
 * breakpoints each time), which is exactly what made toggles feel scattered across the form.
 * A single auto-fit grid + a visible boundary makes every switch cluster look and behave
 * the same everywhere, and reads as one decision panel instead of loose floating controls.
 */
export const SwitchGroup = ({ children, ...props }) => (
    <Box
        display="grid"
        gridTemplateColumns="repeat(auto-fit, minmax(200px, 1fr))"
        gap={3}
        p={3}
        border="1px solid"
        borderColor="gray.200"
        borderRadius={10}
        transition="border-color 0.2s"
        _hover={{ borderColor: "gray.300" }}
        {...props}
    >
        {children}
    </Box>
);

export const SubsectionTitle = ({ children }) => (
    <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Box w={1} h={4} bg="purple.400" borderRadius="full" />
        <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wider" fontWeight={600}>
            {children}
        </Text>
    </Box>
);

export const Grid2 = ({ children, ...props }) => (
    <Box display="grid" gridTemplateColumns={{ base: "1fr", sm: "1fr 1fr" }} gap={4} {...props}>
        {children}
    </Box>
);

export const Grid3 = ({ children, ...props }) => (
    <Box display="grid" gridTemplateColumns={{ base: "1fr", sm: "1fr 1fr 1fr" }} gap={4} {...props}>
        {children}
    </Box>
);
